import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError, MemberBooking, Slot } from '../api';
import Icon, { slotIcon } from '../design/Icon';
import { GymHeader, Ring, Sheet, fmtTime, fmtDateLong, todayStr, addDaysStr, dayLetter, greeting } from '../design/atoms';
import type { MemberGym } from '../hooks/useMemberGyms';

/* SPEC 01 Home: gym header · greeting · membership-efficiency hero ·
   next booking · streak week · curated tiles. */

interface OutletCtx {
  gyms: MemberGym[];
  activeGym: MemberGym | null;
  activeGymId: string | null;
  setActiveGymId: (id: string | null) => void;
  hasClassScheduling: boolean;
  loading: boolean;
}

interface CheckinSummary {
  lastCheckIn: string | null;
  membershipStartDate: string | null;
  daysCheckedIn: number | null;
  daysSinceMembershipStart: number | null;
  efficiency: number | null;
}

interface CheckinLog {
  checkInTime: string | null;
}

export default function MemberHomePage() {
  const { token, member } = useMemberAuth();
  const { gyms, activeGym, activeGymId, setActiveGymId, hasClassScheduling } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();

  const [summary, setSummary] = useState<CheckinSummary | null>(null);
  const [logs, setLogs] = useState<CheckinLog[]>([]);
  const [nextBooking, setNextBooking] = useState<MemberBooking | null>(null);
  const [curated, setCurated] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [switcher, setSwitcher] = useState(false);
  // Lifecycle override after a successful in-app lead→trial conversion (the
  // gyms list is only refetched on next load).
  const [trialStarted, setTrialStarted] = useState<{ gymId: string; trialEndDate: string } | null>(null);
  const [startingTrial, setStartingTrial] = useState(false);

  const today = todayStr();

  useEffect(() => {
    if (!token || !activeGymId) return;
    setLoading(true);
    const calls: Promise<void>[] = [
      mfetch(token, '/member/checkins', { params: { gymId: activeGymId, limit: '100' } })
        .then(r => {
          setSummary(r.summary);
          setLogs(r.data ?? []);
        })
        .catch(() => setSummary(null)),
    ];
    if (hasClassScheduling) {
      calls.push(
        mfetch(token, '/member/bookings', { params: { gymId: activeGymId, scope: 'upcoming' } })
          .then(r => setNextBooking((r.data as MemberBooking[]).find(b => b.status === 'booked') ?? null))
          .catch(() => setNextBooking(null)),
        mfetch(token, '/member/slots', { params: { gymId: activeGymId, date: today } })
          .then(r => {
            const all: Slot[] = (r.data.groups as { slots: Slot[] }[]).flatMap(g => g.slots);
            setCurated(all.filter(s => s.spotsLeft > 0 && !s.myBookingId).slice(0, 2));
          })
          .catch(() => setCurated([])),
      );
    }
    Promise.allSettled(calls).finally(() => setLoading(false));
  }, [token, activeGymId, hasClassScheduling, today]);

  // Check-in day pills for this week (Mon-Sun) + streak from attendance days
  const checkinDays = useMemo(() => {
    const days = new Set<string>();
    for (const l of logs) {
      if (!l.checkInTime) continue;
      const d = new Date(l.checkInTime);
      days.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    return days;
  }, [logs]);

  const weekPills = useMemo(() => {
    const d = new Date(`${today}T00:00:00`);
    const monday = addDaysStr(today, -((d.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDaysStr(monday, i);
      return { date, letter: dayLetter(date), attended: checkinDays.has(date), isToday: date === today, future: date > today };
    });
  }, [today, checkinDays]);

  const streak = useMemo(() => {
    let current = 0;
    let cursor = checkinDays.has(today) ? today : addDaysStr(today, -1);
    while (checkinDays.has(cursor)) {
      current++;
      cursor = addDaysStr(cursor, -1);
    }
    let best = current;
    const sorted = [...checkinDays].sort();
    let run = 0;
    let prev: string | null = null;
    for (const day of sorted) {
      run = prev !== null && addDaysStr(prev, 1) === day ? run + 1 : 1;
      if (run > best) best = run;
      prev = day;
    }
    return { current, best };
  }, [checkinDays, today]);

  const firstName = (member?.displayName || member?.fullName || 'there').split(' ')[0];
  const daysSinceVisit = summary?.lastCheckIn
    ? Math.floor((new Date(`${today}T00:00:00`).getTime() - new Date(summary.lastCheckIn).setHours(0, 0, 0, 0)) / 86400000)
    : null;
  const lastVisitLabel = summary?.lastCheckIn
    ? new Date(summary.lastCheckIn).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
    : '—';
  const hasMembership = !!summary?.membershipStartDate;

  // Lifecycle at THIS gym (A1's memberStatus): drives which hero renders.
  const memberStatus = trialStarted?.gymId === activeGymId
    ? 'trial'
    : activeGym?.memberStatus ?? (hasMembership ? 'active' : 'inactive');
  const trialEndDate = trialStarted?.gymId === activeGymId
    ? trialStarted.trialEndDate
    : activeGym?.trialEndDate ?? null;
  /** Trial used up and no membership taken — its own hero, per the mobile app. */
  const trialLapsed = memberStatus === 'inactive' && !!trialEndDate && trialEndDate < today;

  const startTrial = async () => {
    if (!token || !activeGymId || startingTrial) return;
    setStartingTrial(true);
    try {
      const r = await mfetch(token, '/member/trial', { method: 'POST', body: { gymId: activeGymId, trialDays: 1 } });
      setTrialStarted({ gymId: activeGymId, trialEndDate: r.data.trialEndDate });
      toast.success('Trial started — see you on the floor!');
    } catch (e) {
      if (e instanceof MemberApiError && e.code === 'TRIAL_ALREADY_USED') {
        toast.error("You've already used your trial here — check out the plans instead.");
      } else if (e instanceof MemberApiError && e.code === 'NOT_A_LEAD') {
        toast.error('Trials are for new visitors — talk to the front desk about a plan.');
      } else {
        toast.error(e instanceof MemberApiError ? e.message : 'Could not start your trial');
      }
    } finally {
      setStartingTrial(false);
    }
  };

  const bookingIsToday = nextBooking?.session.date === today;

  return (
    <div className="mpage">
      {/* Check-in is no longer a tab — the QR lives here, in the gym header. */}
      <GymHeader
        name={activeGym?.displayName || activeGym?.name || 'Your gym'}
        logoUrl={activeGym?.logoUrl}
        onBrandClick={gyms.length > 1 ? () => setSwitcher(true) : undefined}
        right={
          <button className="icon-btn" aria-label="Check in" onClick={() => navigate('/app/checkin')}>
            <Icon n="qr" s={18} c="var(--ink-2)" />
          </button>
        }
      />

      <div className="col" style={{ gap: 2 }}>
        <div className="dp dp-l">{greeting()}, {firstName}</div>
        <div className="sub">
          {fmtDateLong(today)}
          {daysSinceVisit != null && daysSinceVisit > 0 &&
            ` · ${daysSinceVisit} day${daysSinceVisit === 1 ? '' : 's'} since your last visit`}
        </div>
      </div>

      {/* Lifecycle hero: lead → trial CTA · trial → countdown ·
          active/paused → efficiency · inactive → plans browsing */}
      {loading ? (
        <div className="skel" style={{ height: 170, borderRadius: 'var(--r-m)' }} />
      ) : memberStatus === 'lead' ? (
        <div className="card ink" style={{ padding: 18 }}>
          <div className="col" style={{ gap: 8 }}>
            <div className="lbl">Welcome</div>
            <div className="dp dp-m" style={{ color: 'var(--on-ink)' }}>Try {activeGym?.displayName || activeGym?.name} for a day</div>
            <div className="sub wrap">One free day, full access. No card, no commitment.</div>
            <div className="row" style={{ gap: 8, marginTop: 4 }}>
              <button className="btn gold sm" disabled={startingTrial} onClick={startTrial}>
                {startingTrial ? 'Starting…' : 'Start 1-day trial'}
              </button>
              <button className="btn ghost sm" onClick={() => navigate('/app/plans')}>See plans</button>
            </div>
          </div>
        </div>
      ) : memberStatus === 'trial' ? (
        <div className="card ink" style={{ padding: 18 }}>
          <div className="col" style={{ gap: 8 }}>
            <div className="lbl">Trial</div>
            <div className="dp dp-m" style={{ color: 'var(--on-ink)' }}>You're on a trial here</div>
            <div className="sub wrap">
              {trialEndDate === today
                ? 'Your trial ends today — make it count.'
                : trialEndDate
                  ? `Your trial runs until ${fmtDateLong(trialEndDate)}.`
                  : 'Show the QR at the front desk to check in.'}
            </div>
            <div className="row" style={{ gap: 8, marginTop: 4 }}>
              <button className="btn gold sm" onClick={() => navigate('/app/checkin')}>Check in</button>
              <button className="btn ghost sm" onClick={() => navigate('/app/plans')}>See plans</button>
            </div>
          </div>
        </div>
      ) : trialLapsed ? (
        <div className="card ink" style={{ padding: 18 }}>
          <div className="col" style={{ gap: 8 }}>
            <div className="lbl">Trial</div>
            <div className="dp dp-m" style={{ color: 'var(--on-ink)' }}>Your trial has ended</div>
            <div className="sub wrap">
              Liked it here? Pick a plan to keep training at {activeGym?.displayName || activeGym?.name}.
            </div>
            <button className="btn gold sm" style={{ alignSelf: 'flex-start', marginTop: 4 }} onClick={() => navigate('/app/plans')}>
              See plans
            </button>
          </div>
        </div>
      ) : hasMembership ? (
        <div className="card ink" style={{ padding: '18px 18px 16px' }}>
          <div className="between" style={{ alignItems: 'flex-start' }}>
            <div className="col" style={{ gap: 9 }}>
              <div className="lbl">Membership activity</div>
              <div className="row" style={{ alignItems: 'baseline', gap: 7 }}>
                <div className="num" style={{ fontSize: 40, lineHeight: 1, color: 'var(--ink-accent)' }}>
                  {Math.min(100, summary?.efficiency ?? 0)}%
                </div>
                <div className="tiny">of days used</div>
              </div>
              <div className="sub">
                {summary?.daysCheckedIn ?? 0} visits in {summary?.daysSinceMembershipStart ?? 0} days of membership
              </div>
            </div>
            <Ring v={Math.min(100, summary?.efficiency ?? 0)} size={78} w={7} track="color-mix(in srgb, var(--on-ink) 14%, transparent)">
              <div className="num" style={{ fontSize: 17, color: 'var(--on-ink)' }}>{summary?.daysCheckedIn ?? 0}</div>
              <div style={{ fontSize: 8, letterSpacing: '.12em', fontWeight: 700, color: 'color-mix(in srgb, var(--on-ink) 55%, transparent)' }}>VISITS</div>
            </Ring>
          </div>
          <div className="divider" style={{ margin: '15px 0 12px' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              ['Last visit', lastVisitLabel],
              ['Check-ins', String(summary?.daysCheckedIn ?? 0)],
              ['Days in', String(summary?.daysSinceMembershipStart ?? 0)],
            ].map(([l, v]) => (
              <div key={l} className="col" style={{ gap: 3 }}>
                <div className="lbl">{l}</div>
                <div className="num" style={{ fontSize: 16, color: 'var(--on-ink)' }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="card ink" style={{ padding: 18 }}>
          <div className="col" style={{ gap: 8 }}>
            <div className="lbl">Membership</div>
            <div className="dp dp-m" style={{ color: 'var(--on-ink)' }}>
              {memberStatus === 'suspended' ? 'Membership on hold' : 'No active membership'}
            </div>
            <div className="sub wrap">
              {memberStatus === 'suspended'
                ? 'Talk to the front desk to sort this out.'
                : 'Pick a plan that fits — browse what this gym offers.'}
            </div>
            {memberStatus !== 'suspended' && (
              <button className="btn gold sm" style={{ alignSelf: 'flex-start', marginTop: 4 }} onClick={() => navigate('/app/plans')}>
                See plans
              </button>
            )}
          </div>
        </div>
      )}

      {/* Next booking */}
      {hasClassScheduling && !loading && (
        nextBooking ? (
          <div className="card tint" style={{ padding: 14 }}>
            <div className="between">
              <div className="row" style={{ gap: 11, minWidth: 0 }}>
                <div className="li gold">
                  <Icon n={slotIcon(nextBooking.session.class.bookableType, nextBooking.session.class.name)} s={18} c="var(--gold-2)" />
                </div>
                <div className="col" style={{ gap: 1, minWidth: 0 }}>
                  <div className="lbl-d">
                    Next up · {bookingIsToday ? 'today' : fmtDateLong(nextBooking.session.date).split(',')[0]} {fmtTime(nextBooking.session.startTime)}
                  </div>
                  <div className="mid trunc">{nextBooking.session.class.name}</div>
                  <div className="tiny trunc">
                    {[nextBooking.session.room?.name, 'arrive 10 min early'].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>
              {bookingIsToday ? (
                <button className="btn gold sm" onClick={() => navigate('/app/checkin')}>Check in</button>
              ) : (
                <button className="btn ghost sm" onClick={() => navigate('/app/bookings')}>View</button>
              )}
            </div>
          </div>
        ) : (
          <div className="card tint" style={{ padding: 14 }}>
            <div className="between">
              <div className="col" style={{ gap: 1 }}>
                <div className="lbl-d">Today</div>
                <div className="mid">Nothing booked today</div>
              </div>
              <button className="btn gold sm" onClick={() => navigate('/app/book')}>Book</button>
            </div>
          </div>
        )
      )}

      {/* Streak row */}
      {hasMembership && (
        <div className="between" style={{ padding: '0 2px' }}>
          <div className="row" style={{ gap: 9 }}>
            <div className="li gold" style={{ width: 30, height: 30, flex: '0 0 30px' }}>
              <Icon n="flame" s={16} c="var(--gold-2)" />
            </div>
            <div className="col">
              <div className="mid nw">{streak.current}-day streak</div>
              <div className="tiny nw">Best · {streak.best} days</div>
            </div>
          </div>
          <div className="dots">
            {weekPills.map(p => (
              <div key={p.date} className={`dot ${p.attended ? 'on' : p.isToday ? 'soft' : ''}`}>{p.letter}</div>
            ))}
          </div>
        </div>
      )}

      {/* Plan and calendar now have their own tab — no duplicate tiles here. */}

      {/* Curated for you */}
      {curated.length > 0 && (
        <div className="col" style={{ gap: 9 }}>
          <div className="lbl" style={{ paddingLeft: 2 }}>Available today</div>
          <div className="grid2">
            {curated.map(s => (
              <button
                key={s.id}
                className="card"
                style={{ padding: 13, textAlign: 'left' }}
                onClick={() => navigate(`/app/book?date=${s.date}&type=${s.bookableType}`)}
              >
                <div className="col" style={{ gap: 9 }}>
                  <div className="li gold" style={{ width: 32, height: 32, flex: '0 0 32px' }}>
                    <Icon n={slotIcon(s.bookableType, s.name)} s={17} c="var(--gold-2)" />
                  </div>
                  <div className="col" style={{ gap: 2, minWidth: 0 }}>
                    <div className="mid trunc" style={{ fontSize: 13.5 }}>{s.name}</div>
                    <div className="tiny trunc">{s.spotsLeft} spot{s.spotsLeft === 1 ? '' : 's'} · {fmtTime(s.startTime)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {switcher && (
        <Sheet onClose={() => setSwitcher(false)}>
          <div className="dp dp-m">Your gyms</div>
          <div className="col">
            {gyms.map(g => (
              <button
                key={g.id}
                className="lrow"
                onClick={() => {
                  setActiveGymId(g.id);
                  setSwitcher(false);
                }}
              >
                <div className="mark" style={{ width: 34, height: 34, flex: '0 0 34px', fontSize: 16, borderRadius: 11 }}>
                  {g.logoUrl ? <img src={g.logoUrl} alt="" /> : (g.displayName || g.name).charAt(0).toUpperCase()}
                </div>
                <div className="col" style={{ gap: 1 }}>
                  <div className="mid trunc">{g.displayName || g.name}</div>
                </div>
                {g.id === activeGymId && <Icon n="check" s={18} c="var(--gold)" />}
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </div>
  );
}
